import * as React from 'react';
import { useFormModal } from '@repo/form';
import { FormModal } from '@repo/web-form';
import { FormField, Input } from '../../../components/ui/FormField.js';
import { useRecordChallan } from '../network/tax.api.js';
import { useToast } from '../../../components/ui/Toast.js';
import { z } from 'zod';

const schema = z.object({
  challanNo: z.string().min(1, 'Challan number is required'),
  challanDate: z.string().min(1, 'Challan date is required'),
});

interface RecordChallanModalProps {
  id: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export function RecordChallanModal({ id, isOpen, onClose }: RecordChallanModalProps) {
  const { toast } = useToast();
  const recordChallan = useRecordChallan();

  const modal = useFormModal(
    { defaultValues: { challanNo: '', challanDate: new Date().toISOString().split('T')[0] }, schema },
    recordChallan,
    {
      onBeforeSubmit: (value) => ({
        id,
        body: {
          challanNo: value.challanNo,
          challanDate: new Date(value.challanDate).toISOString(),
        },
      }),
      onSuccess: () => {
        toast({ type: 'success', title: 'Challan Recorded', description: 'TDS Challan details saved successfully.' });
        onClose();
      },
      onError: (err) => {
        toast({ type: 'error', title: 'Error', description: err.message || 'Failed to record challan' });
      },
    },
  );

  React.useEffect(() => {
    if (isOpen) {
      modal.form.reset();
    }
  }, [isOpen]);

  return (
    <FormModal open={isOpen} onClose={onClose} title="Record TDS Challan" description="Record the bank challan number and payment date for this TDS deduction." modal={modal} submitLabel="Submit">
      <modal.form.Field
        name="challanNo"
        validators={{
          onChange: ({ value }: { value: any }) => {
            if (!value || value.trim() === '') {
              return 'Challan number is required';
            }
            return undefined;
          },
        }}
        children={(field: any) => (
          <FormField
            label="Challan Number"
            required
            error={modal.fieldError(field.state.meta)}
            htmlFor="challanNo"
          >
            <Input
              id="challanNo"
              placeholder="e.g. 12345"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              error={!!modal.fieldError(field.state.meta)}
              disabled={modal.isSubmitting}
            />
          </FormField>
        )}
      />

      <modal.form.Field
        name="challanDate"
        validators={{
          onChange: ({ value }: { value: any }) => {
            if (!value) {
              return 'Challan date is required';
            }
            const selectedDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            selectedDate.setHours(0, 0, 0, 0);
            if (selectedDate > today) {
              return 'Challan date cannot be in the future';
            }
            return undefined;
          },
        }}
        children={(field: any) => (
          <FormField
            label="Challan Date"
            required
            error={modal.fieldError(field.state.meta)}
            htmlFor="challanDate"
          >
            <Input
              id="challanDate"
              type="date"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              error={!!modal.fieldError(field.state.meta)}
              disabled={modal.isSubmitting}
            />
          </FormField>
        )}
      />
    </FormModal>
  );
}
